/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CrudAPI, CrudService } from '@core/api';
// Known leak, ratcheted rather than excused: this value import puts
// DevExtreme's runtime in the NativeScript bundle, measured at 1.53 MiB (9.3%
// of vendor.js) in docs/analysis/mobile-bundle-devextreme-leak.md. The rule errors on
// any NEW devextreme import here; removing these eight disables is what fixing
// that leak means.
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import notify from 'devextreme/ui/notify';
import {
  BehaviorSubject,
  Observable,
  catchError,
  map,
  mergeMap,
  of,
  shareReplay,
  tap,
  throwError,
} from 'rxjs';
import {
  conditions,
  drives,
  goods_types,
  tangible_asset_types,
  units,
} from '../datas';
import {
  AutofillReplayInfo,
  InventoryBulkPatch,
  InventoryItem,
  InventoryItemPriceHistory,
  InventoryItemSuggestions,
  InventoryItemWithoutThirdpartyRights,
  NO_INVENTORY_ITEM_SUGGESTIONS,
  RgCalculatedPricePayload,
  ThirdpartyRight,
  defaultInventoryItem,
  sortedInventoryItemSuggestions,
} from '../models';

/** One item the server refused to persist, with a reason to show the user. */
export type InventoryBulkUpdateFailure = {
  id: number;
  message: string;
};

/**
 * The bulk update response body, exactly as
 * `api/component/inventoryItem/bulkUpdate` returns it. `updated` and `failed`
 * together account for every submitted id — anything missing from both is
 * reconciled by the caller as "state unknown".
 *
 * See docs/specs/2026-07-31-001-api-inventory-bulk-update-spec.md § 3.
 */
export type InventoryBulkUpdateResponse = {
  data: {
    updated: number[];
    failed: InventoryBulkUpdateFailure[];
  };
  message?: string;
};

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  private inventoryApi!: CrudAPI<InventoryItem>;
  private inventoryItems = new BehaviorSubject<InventoryItem[]>([]);
  private inventoryItemsWithoutThirdpartyRights = new BehaviorSubject<
    InventoryItemWithoutThirdpartyRights[]
  >([]);
  private inventoryItem = new BehaviorSubject<InventoryItem>(
    defaultInventoryItem
  );
  private thirdPartyRights = new BehaviorSubject<ThirdpartyRight[]>([]);

  public readonly inventoryItems$ = this.inventoryItems.asObservable();
  public readonly inventoryItemsWithoutThirdpartyRights$ =
    this.inventoryItemsWithoutThirdpartyRights.asObservable();
  public readonly inventoryItem$ = this.inventoryItem.asObservable();
  public readonly thirdPartyRights$ = this.thirdPartyRights.asObservable();

  public units$ = of(units).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => units.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  public conditions$ = of(conditions).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => conditions.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  public tangibleAssetTypes$ = of(tangible_asset_types).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => tangible_asset_types.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  public goodsTypes$ = of(goods_types).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => goods_types.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  public drives$ = of(drives).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => drives.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  constructor(
    private crud: CrudService,
    private translateService: TranslateService
  ) {
    this.inventoryApi = crud.forEndpoint('api/crud/inventoryItem');
  }

  /**
   * Loads the project's inventory items into `inventoryItems$` and hands the
   * loaded list to the caller.
   *
   * Unlike the rest of this service it does **not** swallow the error into a
   * neutral value: a failed reload would otherwise empty the grid and arrive at
   * the caller's `next` looking like a successful load of nothing. On error the
   * previously loaded items stay in the subject and the error propagates, so
   * the caller can report it (R31).
   */
  public loadInventoryItems(projectId: number): Observable<InventoryItem[]> {
    return this.inventoryApi
      .readMany(
        `with=fullViewFile&search=projectLocation.project_id:${projectId}&searchFields=projectLocation.project_id:=`
      )
      .pipe(
        map(({ data }: any) => data as InventoryItem[]),
        tap((x) => this.inventoryItems.next(x)),
        catchError((e) => {
          notify(e.message, 'error');
          return throwError(() => e);
        })
      );
  }

  /**
   * Writes many per-item partial patches in one request (R21).
   *
   * The one method in this service that hands its errors to the caller: the
   * bulk form distinguishes all, partial and no datasets written, and the house
   * `catchError(() => of({}))` would report a `500` as a success (KTD10).
   * Neither notifies nor pushes into a subject — the caller owns the message
   * and the reload.
   */
  public bulkUpdateInventoryItems(
    patches: readonly InventoryBulkPatch[]
  ): Observable<InventoryBulkUpdateResponse> {
    const items = patches.map((patch) => {
      // Defence in depth behind `buildInventoryBulkPatches`, which already
      // drops `value_factor`. It is not a stored column (see the API spec
      // § 2.2), so nothing may leak it onto the wire from any other caller.
      const item: InventoryBulkPatch = { ...patch };
      delete item.value_factor;
      return item;
    });

    return this.crud
      .forEndpoint('api/component/inventoryItem/bulkUpdate')
      .createOne({ items });
  }

  public loadInventoryItemsWithoutThirdpartyRights(projectId: number) {
    this.crud
      .readMany(
        `api/component/inventoryItem/listWithoutThirdpartyRight`,
        `?project_id=${projectId}`
      )
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of([] as InventoryItem[]);
        })
      )
      .subscribe((x) => this.inventoryItemsWithoutThirdpartyRights.next(x));
  }

  public loadInventoryItem(itemId: number | null) {
    this.resetInventoryItem();
    if (itemId !== null) {
      this.inventoryApi
        .readOne(
          itemId,
          'with=files%3BfullViewFile%3BinventoryItemThirdpartyRights%3BinventoryItemThirdpartyRights.files'
        )
        .pipe(
          map(({ data }: any) => data),
          catchError((e) => {
            notify(e.message, 'error');
            return of(null);
          })
        )
        .subscribe((x) => {
          if (x) {
            this.inventoryItem.next(x);
          }
        });
    }
  }

  public loadInventoryItemByUid(itemUid: string | null) {
    if (itemUid !== null) {
      this.crud
        .readOne(`api/component/inventoryItem/view`, itemUid)
        .pipe(
          map(({ data }: any) => data),
          catchError((e) => {
            notify(e.error.message, 'error');
            return of(null);
          })
        )
        .subscribe((x) => {
          if (x) {
            this.inventoryItem.next(x);
          }
        });
    }
  }

  public createInventoryItem(data: Partial<InventoryItem>) {
    return this.inventoryApi
      .createOne({
        ...data,
        //@ts-ignore
        files: data.files instanceof Array ? data.files.map((x) => x.id) : [],
        //@ts-ignore
        inventoryItemThirdpartyRights:
          data.inventory_item_thirdparty_rights instanceof Array
            ? data.inventory_item_thirdparty_rights.map((x) => x.id)
            : [],
      })
      .pipe(
        catchError((e) => {
          notify(e.message, 'error');
          return of({});
        })
      );
  }

  public deleteInventoryItem(id: number) {
    return this.inventoryApi.deleteOne(id).pipe(
      catchError((e) => {
        notify(e.message, 'error');
        return of({});
      })
    );
  }

  /**
   * Writes one item and lets the failure reach the caller (KTD10).
   *
   * The grid's inline save has to tell a failed `PUT` from a successful one —
   * it clears the pending edits and reloads only when the write landed — and
   * `updateInventoryItem` cannot say, because it swallows the failure into
   * `of({})` and arrives at the caller's `next` looking like a success.
   *
   * Notifying is the caller's, matching `bulkUpdateInventoryItems`.
   * `updateInventoryItem` keeps its own notify and its own swallow, so the
   * detail page it also serves is unaffected.
   */
  public updateInventoryItemOrFail(
    itemId: number,
    data: Partial<InventoryItem>
  ) {
    return this.inventoryApi.updateOne(itemId, this.withRelationIds(data));
  }

  public updateInventoryItem(itemId: number, data: Partial<InventoryItem>) {
    return this.updateInventoryItemOrFail(itemId, data).pipe(
      catchError((e) => {
        notify(this.translateService.instant(e.message), 'error');
        return of({});
      })
    );
  }

  /** The API takes ids for these two relations, not the loaded objects. */
  private withRelationIds(data: Partial<InventoryItem>) {
    if (data.files) {
      //@ts-ignore
      data.files =
        data.files instanceof Array ? data.files.map((x) => x.id) : [];
    }

    if (data.inventory_item_thirdparty_rights) {
      //@ts-ignore
      data.inventoryItemThirdpartyRights =
        data.inventory_item_thirdparty_rights instanceof Array
          ? data.inventory_item_thirdparty_rights.map((x) => x.id)
          : [];
    }

    return data;
  }

  public checkInventoryNumber(itemNumber: number, projectId: number) {
    return this.crud
      .readMany(
        `api/InventoryItemIdentifications/checkIdentification?projectId=${projectId}&inventoryNumber=${itemNumber}`
      )
      .pipe(
        catchError((e) => {
          notify(this.translateService.instant(e.message), 'error');
          return throwError(() => e);
        })
      );
  }

  /**
   * The title and brand values the project already uses, for the item form.
   * A failure answers empty lists without a message: both fields then work as
   * plain text fields, and nothing typed is at stake.
   */
  public loadSuggestions(
    projectId: number
  ): Observable<InventoryItemSuggestions> {
    return this.crud
      .readMany(
        `api/component/inventoryItem/suggestions?project_id=${projectId}`
      )
      .pipe(
        map(({ data }: any) => sortedInventoryItemSuggestions(data)),
        catchError(() => of(NO_INVENTORY_ITEM_SUGGESTIONS))
      );
  }

  public getNextAndPreviousInventoryItemIds(itemId: number) {
    return this.crud
      .readMany(
        `api/component/inventoryItem/getNextAndPreviousInventoryItemIds/${itemId}`
      )
      .pipe(
        catchError((e) => {
          notify(e.message, 'error');
          return of([]);
        })
      );
  }

  public loadPriceHistory(itemId: number) {
    return this.crud
      .readOne(`api/component/InventoryItemPriceCalculation`, itemId)
      .pipe(
        map(({ data }: any) => data as InventoryItemPriceHistory[]),
        catchError((e) => {
          notify(
            this.translateService.instant(e?.error?.message ?? e.message),
            'error'
          );
          return of([] as InventoryItemPriceHistory[]);
        })
      );
  }

  /**
   * Manually run the internal price calculation for an item. The backend runs
   * it synchronously, so the new history entry exists once this completes.
   */
  public triggerInternalPriceQuery(itemId: number) {
    return this.crud
      .createOne(`api/component/InventoryItemPriceCalculation/${itemId}/internal`, {})
      .pipe(
        catchError((e) => {
          notify(
            this.translateService.instant(e?.error?.message ?? e.message),
            'error'
          );
          return of(null);
        })
      );
  }

  /**
   * Trigger an on-demand GPT price research for an item. The backend runs the
   * lookup asynchronously, so the caller re-fetches the history afterwards.
   */
  public triggerGptPriceQuery(itemId: number) {
    return this.crud
      .createOne(`api/component/InventoryItemPriceCalculation/${itemId}/gpt`, {})
      .pipe(
        catchError((e) => {
          notify(
            this.translateService.instant(e?.error?.message ?? e.message),
            'error'
          );
          return of(null);
        })
      );
  }

  /**
   * Trigger an on-demand Geizhals price lookup for an item. The backend runs the
   * lookup asynchronously, so the caller re-fetches the history afterwards.
   */
  public triggerGeizhalsPriceQuery(itemId: number) {
    return this.crud
      .createOne(`api/component/InventoryItemPriceCalculation/${itemId}/geizhals`, {})
      .pipe(
        catchError((e) => {
          notify(
            this.translateService.instant(e?.error?.message ?? e.message),
            'error'
          );
          return of(null);
        })
      );
  }

  /**
   * Run the synchronous "rg-calculated" Zeitwert calculation for an item from
   * the submitted form parameters. The backend stores a new history entry and
   * returns it (incl. id), so the caller can immediately apply it to the item.
   */
  public triggerRgCalculatedPriceQuery(
    itemId: number,
    payload: RgCalculatedPricePayload
  ) {
    return this.crud
      .createOne(
        `api/component/InventoryItemPriceCalculation/${itemId}/rg-calculated`,
        payload
      )
      .pipe(
        map(({ data }: any) => data as InventoryItemPriceHistory),
        catchError((e) => {
          notify(
            this.translateService.instant(e?.error?.message ?? e.message),
            'error'
          );
          return of(null);
        })
      );
  }

  /**
   * Apply a price-history entry to the item: its price becomes the
   * continuation_value and the liquidation_value is derived from it. Returns the
   * updated InventoryItem.
   */
  public applyPriceHistoryToItem(itemId: number, historyId: number) {
    return this.crud
      .createOne(
        `api/component/InventoryItemPriceCalculation/${itemId}/apply/${historyId}`,
        {}
      )
      .pipe(
        map(({ data }: any) => data as InventoryItem),
        catchError((e) => {
          notify(
            this.translateService.instant(e?.error?.message ?? e.message),
            'error'
          );
          return of(null);
        })
      );
  }

  /**
   * What a replay of this item would run with, for the dialog to show before
   * anything is spent. The scope is the reason it exists: it decides which
   * prompt block the analysis runs, it is not stored on the item, and for all
   * but a handful of items nothing is recoverable — so the appraiser has to
   * choose and needs to see that.
   */
  public getAutofillReplayInfo(itemId: number) {
    return this.crud
      .readMany(`api/component/inventoryItem/${itemId}/autofillReplay`)
      .pipe(
        map(({ data }: any) => data as AutofillReplayInfo),
        catchError((e) => {
          notify(
            this.translateService.instant(e?.error?.message ?? e.message),
            'error'
          );
          return of(null);
        })
      );
  }

  /**
   * Re-run the AI autofill for an item that already exists. The backend queues
   * the analysis and writes the result back onto the SAME record — uid and
   * item_number do not change — so the caller only gets "it started" and
   * reloads the item afterwards. Nothing to poll.
   *
   * Only the AI-owned columns (title, brand, type, color, description,
   * dimensions, weight) are replaced; appraiser-entered values stay. That is
   * what the confirmation dialog in the caller has to say out loud, because the
   * call costs money and can overwrite a hand correction.
   *
   * `scope` picks the prompt block. null leaves the server to recover the scope
   * of the item's own last call, which for most items means none at all.
   */
  public triggerAutofillReplay(itemId: number, scope: string | null = null) {
    return this.crud
      .createOne(`api/component/inventoryItem/${itemId}/autofillReplay`, {
        scope,
      })
      .pipe(
        catchError((e) => {
          notify(
            this.translateService.instant(e?.error?.message ?? e.message),
            'error'
          );
          return of(null);
        })
      );
  }

  public resetInventoryItem() {
    this.inventoryItem.next({ ...defaultInventoryItem, files: [], inventory_item_thirdparty_rights: [] });
  }
}
